import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Globe2, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LanguagesButton({ video, languages, onSaved }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [customVoices, setCustomVoices] = useState([]);
  const [dubVoice, setDubVoice] = useState(
    String(video.voice || "").startsWith("11l:") ? video.voice : ""
  );
  useEffect(() => {
    if (open) axios.get(`${API}/voices/custom`).then((r) => setCustomVoices(r.data)).catch(() => {});
  }, [open]);
  const source = (video.language || "en").toLowerCase();
  const all = (languages || []).map((l) => l.code);
  const [picked, setPicked] = useState(
    () => new Set(video.allowed_langs?.length ? video.allowed_langs : all)
  );

  const toggle = (code) => {
    if (code === source) return;
    setPicked((prev) => {
      const next = new Set(prev);
      next.has(code) ? next.delete(code) : next.add(code);
      return next;
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      const offersAll = all.every((c) => picked.has(c));
      const res = await axios.post(`${API}/videos/${video.id}/allowed-langs`, {
        langs: offersAll ? [] : [...picked],
      });
      const dv = await axios.post(`${API}/videos/${video.id}/dub-voice`, { voice: dubVoice || null });
      const redubs = dv.data?.redubbing?.length || 0;
      toast.success(
        redubs > 0
          ? `Saved — re-dubbing ${redubs} language${redubs > 1 ? "s" : ""} with the new narrator voice (runs in the background)`
          : res.data.allowed_langs
            ? `Viewers now see ${res.data.allowed_langs.length} languages for this story`
            : "All 14 languages offered"
      );
      setOpen(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save languages");
    } finally {
      setBusy(false);
    }
  };

  const restricted = !!video.allowed_langs?.length;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="text-[11px] uppercase tracking-[0.18em] py-2 rounded-lg border border-white/10 hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition-colors flex items-center justify-center gap-1.5"
        data-testid={`library-languages-${video.id}`}
        title="Choose which languages viewers can pick for this story"
      >
        <Globe2 className="w-3.5 h-3.5" />
        {restricted ? `Langs · ${video.allowed_langs.length}` : "Languages"}
      </button>
      {open && createPortal(
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 p-4 overflow-y-auto" onClick={() => setOpen(false)}>
          <div className="w-full max-w-md rounded-xl border border-white/10 bg-[#14120d] p-5 my-8" onClick={(e) => e.stopPropagation()} data-testid="languages-modal">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-display text-lg">Languages offered</h3>
              <button onClick={() => setOpen(false)} data-testid="languages-modal-close"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-xs text-[color:var(--muted)] mb-4">
              Viewers of “{video.title}” can only pick the languages ticked here. Untick everything except a few to launch small and add more when readers ask.
            </p>
            {customVoices.length > 0 && (
              <div className="mb-4 rounded-lg border border-white/10 p-3" data-testid="dub-voice-block">
                <div className="text-xs uppercase tracking-widest text-[color:var(--muted)] mb-1.5">Dub narrator</div>
                <select
                  value={dubVoice}
                  onChange={(e) => setDubVoice(e.target.value)}
                  className="w-full bg-black/30 border border-white/10 rounded px-2 py-2 text-sm"
                  data-testid="dub-voice-select"
                >
                  <option value="">Automatic — best voice per language</option>
                  {customVoices.map((cv) => (
                    <option key={cv.id} value={`11l:${cv.voice_id}`}>{cv.name} — cloned voice (all languages)</option>
                  ))}
                </select>
                <p className="text-[10px] text-[color:var(--muted)] mt-1.5 leading-relaxed">
                  A cloned voice speaks every dubbed language itself. The original audio of publish-as-is stories is never touched.
                </p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-1.5">
              {(languages || []).map((l) => (
                <label key={l.code} className={`flex items-center gap-2 rounded px-2 py-1.5 text-sm cursor-pointer hover:bg-white/[0.05] ${l.code === source ? "opacity-70" : ""}`}>
                  <input
                    type="checkbox"
                    checked={picked.has(l.code)}
                    disabled={l.code === source}
                    onChange={() => toggle(l.code)}
                    className="w-4 h-4 accent-amber-400"
                    data-testid={`lang-check-${l.code}`}
                  />
                  {l.name}{l.code === source && <span className="text-[10px] text-[color:var(--muted)]">original</span>}
                </label>
              ))}
            </div>
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => setPicked(new Set(all))}
                className="text-xs px-3 py-2 rounded border border-white/15"
                data-testid="languages-offer-all"
              >
                Offer all
              </button>
              <button
                onClick={save}
                disabled={busy}
                className="btn-primary text-sm px-4 py-2 flex-1 disabled:opacity-50"
                data-testid="languages-save"
              >
                {busy ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
